import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import TextInput from '../../BaseComponents/TextInput';
import Select, { SelectOption } from '../../BaseComponents/Select';
import Button from '../../BaseComponents/Button';
import './AdminModal.scss';
import { emailRegexp } from '../../../utils/constants';
import { createAdmin, updateAdmin } from '../../../api/admin';
import { toast } from 'react-toastify';
import { getTeamsList } from '../../../api/teams';

import { AdminMember } from '../../../api/admin';

type TypeName = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  adminToEdit?: AdminMember | null;
};

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: SelectOption | null;
}

const AddAdminModal: React.FC<TypeName> = ({ isOpen, onClose, onSuccess, adminToEdit }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<SelectOption | null>(null);
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);

  const roleOptions: SelectOption[] = [
    { key: 'admin', value: 'Admin' },
  ];
  const validate = (values: FormValues) => {
    const errors: Partial<FormValues> = {};
    if (!values.firstName) errors.firstName = 'First Name is required';
    if (!values.lastName) errors.lastName = 'Last Name is required';
    if (!values.email) {
      errors.email = 'Email is required';
    } else if (!emailRegexp.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    // Role validation is handled by the button disabled state for now,
    // but can be added here if needed for formik.errors.role
    return errors;
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: adminToEdit?.first_name || '',
      lastName: adminToEdit?.last_name || '',
      email: adminToEdit?.email || '',
      role: adminToEdit ? (roleOptions.find(opt => opt.key === adminToEdit.role) || roleOptions[0]) : roleOptions[0],
    },
    validate,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      setFormError(null);
      try {
        if (adminToEdit) {
          await updateAdmin(adminToEdit.id, {
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            team_id: selectedTeam?.key || null
          });
          toast.success('Admin updated successfully');
        } else {
          await createAdmin({
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            team_id: selectedTeam?.key || null
          });
          toast.success('Admin created successfully');
        }

        if (onSuccess) onSuccess();
        onClose();
        resetForm();
      } catch (error: any) {
        console.error('Error saving admin:', error);
        setFormError(error.message || 'Failed to save admin');
        toast.error(error.message || 'Failed to save admin');
      } finally {
        setIsLoading(false);
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  const { resetForm } = formik;

  useEffect(() => {
    if (!isOpen) return;

    const setupModal = async () => {
      setFormError(null);
      resetForm();

      try {
        const response = await getTeamsList(0, 100);
        const options: SelectOption[] = response.teams.map((team) => ({
          key: team.id,
          value: team.name,
        }));
        setTeamOptions(options);

        const adminTeamId = adminToEdit?.team_id || '';
        const adminTeamName = adminToEdit?.team_name || '';

        if (adminTeamId) {
          const matchedById = options.find((opt) => opt.key === adminTeamId);
          setSelectedTeam(
            matchedById || { key: adminTeamId, value: adminTeamName || adminTeamId }
          );
        } else if (adminTeamName) {
          const matchedByName = options.find((opt) => opt.value === adminTeamName);
          setSelectedTeam(
            matchedByName || { key: adminTeamName, value: adminTeamName }
          );
        } else {
          setSelectedTeam(null);
        }
      } catch (error: any) {
        setTeamOptions([]);
        setSelectedTeam(null);
        toast.error(error?.message || 'Failed to load teams');
      }
    };

    setupModal();
  }, [isOpen, adminToEdit, resetForm]);

  const isButtonDisabled = !formik.values.firstName || !formik.values.lastName || !formik.values.email || !formik.values.role;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setFormError(null);
        onClose();
      }}
      title={adminToEdit ? 'Edit administrator' : 'Add administrator'}
      width='520px'
    >
      <form
        onSubmit={formik.handleSubmit}
        noValidate
        className='admin-modal-container'
      >
        <div className='main-info-container'>
          <div className='inputs-wrapper'>
            <div className='input-item'>
              <div className='section-title'>First Name *</div>
              <TextInput
                name='firstName'
                placeholder='Enter first name'
                type='text'
                isError={formik.submitCount > 0 && !!formik.errors.firstName}
                errorMessage={
                  formik.submitCount > 0 ? (formik.errors.firstName as string) : undefined
                }
                onChange={(e) => {
                  formik.handleChange(e);
                  setFormError(null);
                  if (formik.submitCount > 0) formik.setFieldError('firstName', '');
                }}
                value={formik.values.firstName}
              />
            </div>

            <div className='input-item'>
              <div className='section-title'>Last Name *</div>
              <TextInput
                name='lastName'
                placeholder='Enter last name'
                type='text'
                isError={formik.submitCount > 0 && !!formik.errors.lastName}
                errorMessage={
                  formik.submitCount > 0 ? (formik.errors.lastName as string) : undefined
                }
                onChange={(e) => {
                  formik.handleChange(e);
                  setFormError(null);
                  if (formik.submitCount > 0) formik.setFieldError('lastName', '');
                }}
                value={formik.values.lastName}
              />
            </div>

            <div className='input-item'>
              <div className='section-title'>Email *</div>
              <TextInput
                name='email'
                placeholder='Enter email'
                type='email'
                isError={formik.submitCount > 0 && !!formik.errors.email}
                errorMessage={
                  formik.submitCount > 0 ? (formik.errors.email as string) : undefined
                }
                onChange={(e) => {
                  formik.handleChange(e);
                  setFormError(null);
                  if (formik.submitCount > 0) formik.setFieldError('email', '');
                }}
                value={formik.values.email}
              />
            </div>


            <div className='form-item'>
              <div className='section-title'>Role *</div>
              <Select
                name='role'
                placeholder='Select Role'
                options={roleOptions}
                selectedOption={formik.values.role || { key: '', value: '' }}
                onChange={(option) => formik.setFieldValue('role', option)}
                isError={!!(formik.touched.role && formik.errors.role)}
                errorMessage={formik.errors.role as string}
              />
            </div>

            <div className='form-item'>
              <div className='section-title'>Team</div>
              <Select
                name='team'
                placeholder='Select Team'
                options={teamOptions}
                selectedOption={selectedTeam || { key: '', value: '' }}
                onChange={(option) => setSelectedTeam(option)}
                isError={false}
              />
            </div>
          </div>
        </div>

        <div className='btn-wrapper'>
          <Button
            text={adminToEdit ? 'Save changes' : 'Add administrator'}
            isLoading={isLoading}
            isDisabled={isButtonDisabled}
            type='submit'
          />
          {formError && (
            <div className='error-message'>
              {formError}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddAdminModal;
